/**
 * Marketing & SEO screen (M8) — campaigns + landing pages / SEO tooling.
 *
 * Two panels, layered top-down:
 *   1. Campaigns panel (M8) — a table of every campaign (name, channel, status,
 *      budget, spend, remaining, impressions, clicks, CTR %, conversions,
 *      conversion-rate %, CPC) with a per-row status control, a "Record stats"
 *      mini-form to add impressions/clicks/conversions/spend, and a delete
 *      button, plus a "New campaign" form.
 *   2. Landing pages & SEO panel (M8) — a table of every page (slug, title,
 *      published toggle, an "Audit SEO" button that reveals the score + issues
 *      inline) plus a "New page" form (title, optional slug — server slugifies —
 *      meta description, heading, body). A DUPLICATE_SLUG error surfaces through
 *      the shared {@link UserMessage}.
 *
 * All backend access goes through the fetch-based `../services.js` layer (web
 * boundary: no `@avos/core-api` import). API error envelopes surface through the
 * shared accessible {@link UserMessage} component. Money is formatted with
 * {@link formatMoney} after `Number(x)` on the minor-unit strings.
 *
 * SSR-safe: the initial render is a pure loading state; every network call runs
 * inside an effect or an event handler, so the server render matches hydration.
 */
import {
  useCallback,
  useEffect,
  useState,
  type FormEvent,
  type ReactElement,
} from "react";
import {
  UserMessage,
  toUserMessage,
  type UserMessageModel,
} from "../../components/UserMessage.js";
import {
  apiAuditPage,
  apiCreateCampaign,
  apiCreateLandingPage,
  apiDeleteCampaign,
  apiDeleteLandingPage,
  apiListCampaigns,
  apiListLandingPages,
  apiRecordCampaignStats,
  apiSetCampaignStatus,
  apiSetPagePublished,
  asError,
  formatMoney,
  type Campaign,
  type CampaignChannel,
  type CampaignStatus,
  type LandingPage,
  type SeoAudit,
} from "../services.js";

/** The channels, with human labels, for the form + table. */
const CHANNELS: readonly {
  readonly value: CampaignChannel;
  readonly label: string;
}[] = [
  { value: "email", label: "Email" },
  { value: "social", label: "Social" },
  { value: "search", label: "Search" },
  { value: "display", label: "Display" },
];

/** The statuses, with human labels, for the filter + row select. */
const STATUSES: readonly {
  readonly value: CampaignStatus;
  readonly label: string;
}[] = [
  { value: "draft", label: "Draft" },
  { value: "scheduled", label: "Scheduled" },
  { value: "active", label: "Active" },
  { value: "paused", label: "Paused" },
  { value: "completed", label: "Completed" },
];

/** Human label for a channel value. */
function channelLabel(channel: string): string {
  return CHANNELS.find((c) => c.value === channel)?.label ?? channel;
}

/** Human label for a status value. */
function statusLabel(status: string): string {
  return STATUSES.find((s) => s.value === status)?.label ?? status;
}

/** Format a `[0,1]` ratio as a percentage string, e.g. 0.1234 -> "12.3%". */
function formatPct(ratio: number): string {
  return `${(ratio * 100).toFixed(1)}%`;
}

/** Parse a decimal major-unit string into an integer minor-unit string, or "0". */
function toMinorString(major: string): string {
  const trimmed = major.trim();
  if (trimmed === "") {
    return "0";
  }
  const num = Number(trimmed);
  if (!Number.isFinite(num) || num < 0) {
    return "0";
  }
  return String(Math.round(num * 100));
}

// ===========================================================================
// New campaign form (M8)
// ===========================================================================

function NewCampaignForm({
  onCreate,
  busy,
}: {
  readonly onCreate: (input: {
    name: string;
    channel: CampaignChannel;
    budgetMinor: string;
  }) => void;
  readonly busy: boolean;
}): ReactElement {
  const [name, setName] = useState("");
  const [channel, setChannel] = useState<CampaignChannel>("email");
  const [budget, setBudget] = useState("");

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    const trimmedName = name.trim();
    if (trimmedName === "") {
      return;
    }
    onCreate({
      name: trimmedName,
      channel,
      budgetMinor: toMinorString(budget),
    });
    setName("");
    setBudget("");
  }

  return (
    <form className="console-form" onSubmit={handleSubmit} noValidate>
      <p className="console-form__title">New campaign</p>
      <div className="console-form__row">
        <div className="console-field">
          <label htmlFor="mkt-new-name">Name</label>
          <input
            id="mkt-new-name"
            value={name}
            placeholder="Summer direct-booking push"
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="console-field">
          <label htmlFor="mkt-new-channel">Channel</label>
          <select
            id="mkt-new-channel"
            value={channel}
            onChange={(e) => setChannel(e.target.value as CampaignChannel)}
          >
            {CHANNELS.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </div>
        <div className="console-field">
          <label htmlFor="mkt-new-budget">Budget (major units)</label>
          <input
            id="mkt-new-budget"
            type="number"
            min="0"
            step="0.01"
            value={budget}
            placeholder="50000"
            onChange={(e) => setBudget(e.target.value)}
          />
        </div>
      </div>
      <button type="submit" className="console-btn" disabled={busy}>
        {busy ? "Creating…" : "Create campaign"}
      </button>
    </form>
  );
}

// ===========================================================================
// Record-stats mini-form (M8)
// ===========================================================================

function RecordStatsCell({
  campaign,
  onRecord,
  busy,
}: {
  readonly campaign: Campaign;
  readonly onRecord: (
    campaign: Campaign,
    input: {
      impressions?: number;
      clicks?: number;
      conversions?: number;
      spendMinor?: string;
    },
  ) => void;
  readonly busy: boolean;
}): ReactElement {
  const [impressions, setImpressions] = useState("");
  const [clicks, setClicks] = useState("");
  const [conversions, setConversions] = useState("");
  const [spend, setSpend] = useState("");

  function num(value: string): number | undefined {
    const trimmed = value.trim();
    if (trimmed === "") {
      return undefined;
    }
    const n = Number(trimmed);
    return Number.isFinite(n) && n >= 0 ? Math.floor(n) : undefined;
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    const input: {
      impressions?: number;
      clicks?: number;
      conversions?: number;
      spendMinor?: string;
    } = {};
    const imp = num(impressions);
    if (imp !== undefined) {
      input.impressions = imp;
    }
    const clk = num(clicks);
    if (clk !== undefined) {
      input.clicks = clk;
    }
    const cnv = num(conversions);
    if (cnv !== undefined) {
      input.conversions = cnv;
    }
    if (spend.trim() !== "") {
      input.spendMinor = toMinorString(spend);
    }
    onRecord(campaign, input);
    setImpressions("");
    setClicks("");
    setConversions("");
    setSpend("");
  }

  return (
    <form className="console-inline-form" onSubmit={handleSubmit} noValidate>
      <label htmlFor={`mkt-imp-${campaign.id}`} className="console-sr-only">
        Impressions to add for {campaign.name}
      </label>
      <input
        id={`mkt-imp-${campaign.id}`}
        type="number"
        min="0"
        value={impressions}
        placeholder="Impr."
        onChange={(e) => setImpressions(e.target.value)}
      />
      <label htmlFor={`mkt-clk-${campaign.id}`} className="console-sr-only">
        Clicks to add for {campaign.name}
      </label>
      <input
        id={`mkt-clk-${campaign.id}`}
        type="number"
        min="0"
        value={clicks}
        placeholder="Clicks"
        onChange={(e) => setClicks(e.target.value)}
      />
      <label htmlFor={`mkt-cnv-${campaign.id}`} className="console-sr-only">
        Conversions to add for {campaign.name}
      </label>
      <input
        id={`mkt-cnv-${campaign.id}`}
        type="number"
        min="0"
        value={conversions}
        placeholder="Conv."
        onChange={(e) => setConversions(e.target.value)}
      />
      <label htmlFor={`mkt-spd-${campaign.id}`} className="console-sr-only">
        Spend to add for {campaign.name}
      </label>
      <input
        id={`mkt-spd-${campaign.id}`}
        type="number"
        min="0"
        step="0.01"
        value={spend}
        placeholder="Spend"
        onChange={(e) => setSpend(e.target.value)}
      />
      <button
        type="submit"
        className="console-btn console-btn--ghost"
        disabled={busy}
      >
        {busy ? "…" : "Record"}
      </button>
    </form>
  );
}

// ===========================================================================
// Campaigns panel (M8)
// ===========================================================================

function CampaignsPanel({
  campaigns,
  filter,
  onFilter,
  onCreate,
  onStatus,
  onRecord,
  onDelete,
  createBusy,
  statusId,
  statsId,
  deletingId,
}: {
  readonly campaigns: readonly Campaign[];
  readonly filter: CampaignStatus | "all";
  readonly onFilter: (status: CampaignStatus | "all") => void;
  readonly onCreate: (input: {
    name: string;
    channel: CampaignChannel;
    budgetMinor: string;
  }) => void;
  readonly onStatus: (campaign: Campaign, status: CampaignStatus) => void;
  readonly onRecord: (
    campaign: Campaign,
    input: {
      impressions?: number;
      clicks?: number;
      conversions?: number;
      spendMinor?: string;
    },
  ) => void;
  readonly onDelete: (campaign: Campaign) => void;
  readonly createBusy: boolean;
  readonly statusId: string | null;
  readonly statsId: string | null;
  readonly deletingId: string | null;
}): ReactElement {
  return (
    <section className="console-panel" aria-label="Marketing campaigns">
      <h2 className="console-panel__title">Campaigns</h2>
      <p className="recon-caption">
        Campaigns across email, social, search, and display. Track budget,
        spend, and performance; change status; record new stats; or remove a
        campaign.
      </p>

      <form className="console-form" noValidate>
        <div className="console-form__row">
          <div className="console-field">
            <label htmlFor="mkt-filter-status">Filter by status</label>
            <select
              id="mkt-filter-status"
              value={filter}
              onChange={(e) =>
                onFilter(e.target.value as CampaignStatus | "all")
              }
            >
              <option value="all">All statuses</option>
              {STATUSES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </form>

      {campaigns.length > 0 ? (
        <table className="console-table">
          <caption className="console-sr-only">Marketing campaigns</caption>
          <thead>
            <tr>
              <th scope="col">Name</th>
              <th scope="col">Channel</th>
              <th scope="col">Status</th>
              <th scope="col">Budget</th>
              <th scope="col">Spend</th>
              <th scope="col">Remaining</th>
              <th scope="col">Impr.</th>
              <th scope="col">Clicks</th>
              <th scope="col">CTR</th>
              <th scope="col">Conv.</th>
              <th scope="col">Conv. rate</th>
              <th scope="col">CPC</th>
              <th scope="col">Record stats</th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {campaigns.map((c) => (
              <tr key={c.id}>
                <td>{c.name}</td>
                <td>{channelLabel(c.channel)}</td>
                <td>
                  <label
                    htmlFor={`mkt-status-${c.id}`}
                    className="console-sr-only"
                  >
                    Status for {c.name}
                  </label>
                  <select
                    id={`mkt-status-${c.id}`}
                    value={c.status}
                    disabled={statusId === c.id}
                    onChange={(e) =>
                      onStatus(c, e.target.value as CampaignStatus)
                    }
                  >
                    {STATUSES.map((s) => (
                      <option key={s.value} value={s.value}>
                        {statusLabel(s.value)}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="recon-num">
                  {formatMoney(Number(c.budgetMinor), c.currency)}
                </td>
                <td className="recon-num">
                  {formatMoney(Number(c.spendMinor), c.currency)}
                </td>
                <td className="recon-num">
                  {formatMoney(Number(c.budgetRemainingMinor), c.currency)}
                </td>
                <td className="recon-num">{c.impressions}</td>
                <td className="recon-num">{c.clicks}</td>
                <td className="recon-num">{formatPct(c.ctr)}</td>
                <td className="recon-num">{c.conversions}</td>
                <td className="recon-num">{formatPct(c.conversionRate)}</td>
                <td className="recon-num">
                  {formatMoney(Number(c.cpcMinor), c.currency)}
                </td>
                <td>
                  <RecordStatsCell
                    campaign={c}
                    onRecord={onRecord}
                    busy={statsId === c.id}
                  />
                </td>
                <td>
                  <button
                    type="button"
                    className="console-btn console-btn--ghost"
                    disabled={deletingId === c.id}
                    onClick={() => onDelete(c)}
                  >
                    {deletingId === c.id ? "Deleting…" : "Delete"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="console-empty">
          No campaigns match this filter — create one with the form below.
        </p>
      )}

      <NewCampaignForm onCreate={onCreate} busy={createBusy} />
    </section>
  );
}

// ===========================================================================
// New page form (M8)
// ===========================================================================

function NewPageForm({
  onCreate,
  busy,
}: {
  readonly onCreate: (input: {
    title: string;
    slug?: string;
    metaDescription?: string;
    heading?: string;
    body?: string;
  }) => void;
  readonly busy: boolean;
}): ReactElement {
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [metaDescription, setMetaDescription] = useState("");
  const [heading, setHeading] = useState("");
  const [body, setBody] = useState("");

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    const trimmedTitle = title.trim();
    if (trimmedTitle === "") {
      return;
    }
    const input: {
      title: string;
      slug?: string;
      metaDescription?: string;
      heading?: string;
      body?: string;
    } = { title: trimmedTitle };
    const s = slug.trim();
    if (s !== "") {
      input.slug = s;
    }
    const meta = metaDescription.trim();
    if (meta !== "") {
      input.metaDescription = meta;
    }
    const h = heading.trim();
    if (h !== "") {
      input.heading = h;
    }
    const b = body.trim();
    if (b !== "") {
      input.body = b;
    }
    onCreate(input);
    setTitle("");
    setSlug("");
    setMetaDescription("");
    setHeading("");
    setBody("");
  }

  return (
    <form className="console-form" onSubmit={handleSubmit} noValidate>
      <p className="console-form__title">New landing page</p>
      <div className="console-form__row">
        <div className="console-field">
          <label htmlFor="mkt-page-title">Title</label>
          <input
            id="mkt-page-title"
            value={title}
            placeholder="Beachfront Villa in Goa"
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>
        <div className="console-field">
          <label htmlFor="mkt-page-slug">Slug (optional)</label>
          <input
            id="mkt-page-slug"
            value={slug}
            placeholder="server slugifies if blank"
            onChange={(e) => setSlug(e.target.value)}
          />
        </div>
        <div className="console-field">
          <label htmlFor="mkt-page-heading">Heading</label>
          <input
            id="mkt-page-heading"
            value={heading}
            placeholder="Beachfront Villa in Goa"
            onChange={(e) => setHeading(e.target.value)}
          />
        </div>
      </div>
      <div className="console-form__row">
        <div className="console-field">
          <label htmlFor="mkt-page-meta">Meta description</label>
          <input
            id="mkt-page-meta"
            value={metaDescription}
            placeholder="A concise 50–160 character summary for search results"
            onChange={(e) => setMetaDescription(e.target.value)}
          />
        </div>
        <div className="console-field">
          <label htmlFor="mkt-page-body">Body</label>
          <input
            id="mkt-page-body"
            value={body}
            placeholder="Page content (100+ characters recommended)"
            onChange={(e) => setBody(e.target.value)}
          />
        </div>
      </div>
      <button type="submit" className="console-btn" disabled={busy}>
        {busy ? "Creating…" : "Create page"}
      </button>
    </form>
  );
}

// ===========================================================================
// Landing pages & SEO panel (M8)
// ===========================================================================

function PagesPanel({
  pages,
  audits,
  onCreate,
  onPublish,
  onAudit,
  onDelete,
  createBusy,
  publishId,
  auditId,
  deletingId,
}: {
  readonly pages: readonly LandingPage[];
  readonly audits: Readonly<Record<string, SeoAudit>>;
  readonly onCreate: (input: {
    title: string;
    slug?: string;
    metaDescription?: string;
    heading?: string;
    body?: string;
  }) => void;
  readonly onPublish: (page: LandingPage, published: boolean) => void;
  readonly onAudit: (page: LandingPage) => void;
  readonly onDelete: (page: LandingPage) => void;
  readonly createBusy: boolean;
  readonly publishId: string | null;
  readonly auditId: string | null;
  readonly deletingId: string | null;
}): ReactElement {
  return (
    <section className="console-panel" aria-label="Landing pages and SEO">
      <h2 className="console-panel__title">Landing pages &amp; SEO</h2>
      <p className="recon-caption">
        SEO landing pages to grow direct demand. Publish a page, audit its SEO
        health, or remove it. Slugs are URL-safe and unique per property account.
      </p>

      {pages.length > 0 ? (
        <table className="console-table">
          <caption className="console-sr-only">Landing pages</caption>
          <thead>
            <tr>
              <th scope="col">Slug</th>
              <th scope="col">Title</th>
              <th scope="col">Published</th>
              <th scope="col">SEO</th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {pages.map((p) => {
              const audit = audits[p.id];
              return (
                <tr key={p.id}>
                  <td>{p.slug}</td>
                  <td>{p.title}</td>
                  <td>
                    <label
                      htmlFor={`mkt-pub-${p.id}`}
                      className="console-sr-only"
                    >
                      Published for {p.title}
                    </label>
                    <input
                      id={`mkt-pub-${p.id}`}
                      type="checkbox"
                      checked={p.published}
                      disabled={publishId === p.id}
                      onChange={(e) => onPublish(p, e.target.checked)}
                    />
                  </td>
                  <td>
                    <button
                      type="button"
                      className="console-btn console-btn--ghost"
                      disabled={auditId === p.id}
                      onClick={() => onAudit(p)}
                    >
                      {auditId === p.id ? "Auditing…" : "Audit SEO"}
                    </button>
                    {audit !== undefined ? (
                      <div className="recon-caption" data-seo-audit={p.id}>
                        <p>
                          <strong>Score: {audit.score}/100</strong>
                        </p>
                        {audit.issues.length > 0 ? (
                          <ul>
                            {audit.issues.map((issue, i) => (
                              <li key={i}>{issue}</li>
                            ))}
                          </ul>
                        ) : (
                          <p>No SEO issues found — nicely optimized.</p>
                        )}
                      </div>
                    ) : null}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="console-btn console-btn--ghost"
                      disabled={deletingId === p.id}
                      onClick={() => onDelete(p)}
                    >
                      {deletingId === p.id ? "Deleting…" : "Delete"}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <p className="console-empty">
          No landing pages yet — create one with the form below.
        </p>
      )}

      <NewPageForm onCreate={onCreate} busy={createBusy} />
    </section>
  );
}

// ===========================================================================
// Top-level screen
// ===========================================================================

type LoadState =
  | { readonly kind: "loading" }
  | {
      readonly kind: "ready";
      readonly campaigns: readonly Campaign[];
      readonly pages: readonly LandingPage[];
    }
  | { readonly kind: "error" };

export default function MarketingScreen(): ReactElement {
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [apiError, setApiError] = useState<UserMessageModel | null>(null);
  const [status, setStatus] = useState<UserMessageModel | null>(null);
  const [filter, setFilter] = useState<CampaignStatus | "all">("all");
  const [audits, setAudits] = useState<Record<string, SeoAudit>>({});

  const [createCampaignBusy, setCreateCampaignBusy] = useState(false);
  const [statusId, setStatusId] = useState<string | null>(null);
  const [statsId, setStatsId] = useState<string | null>(null);
  const [deletingCampaignId, setDeletingCampaignId] = useState<string | null>(
    null,
  );

  const [createPageBusy, setCreatePageBusy] = useState(false);
  const [publishId, setPublishId] = useState<string | null>(null);
  const [auditId, setAuditId] = useState<string | null>(null);
  const [deletingPageId, setDeletingPageId] = useState<string | null>(null);

  const load = useCallback(async (): Promise<{
    campaigns: readonly Campaign[];
    pages: readonly LandingPage[];
  }> => {
    const [campaigns, pages] = await Promise.all([
      apiListCampaigns(filter === "all" ? {} : { status: filter }),
      apiListLandingPages(),
    ]);
    return { campaigns, pages };
  }, [filter]);

  const refresh = useCallback(async (): Promise<void> => {
    try {
      const { campaigns, pages } = await load();
      setState({ kind: "ready", campaigns, pages });
    } catch {
      setState({ kind: "error" });
    }
  }, [load]);

  useEffect(() => {
    let active = true;
    load()
      .then(({ campaigns, pages }) => {
        if (active) {
          setState({ kind: "ready", campaigns, pages });
        }
      })
      .catch(() => {
        if (active) {
          setState({ kind: "error" });
        }
      });
    return () => {
      active = false;
    };
  }, [load]);

  const handleCreateCampaign = useCallback(
    async (input: {
      name: string;
      channel: CampaignChannel;
      budgetMinor: string;
    }): Promise<void> => {
      setApiError(null);
      setStatus(null);
      setCreateCampaignBusy(true);
      try {
        const result = await apiCreateCampaign(input);
        const e = asError(result);
        if (e !== null) {
          setApiError(toUserMessage(e));
          return;
        }
        setStatus({
          what: "Campaign created.",
          why: "The campaign has been added in draft.",
          next: "Set it live or record stats as it runs.",
        });
        await refresh();
      } catch {
        setApiError(networkError("create the campaign"));
      } finally {
        setCreateCampaignBusy(false);
      }
    },
    [refresh],
  );

  const handleStatus = useCallback(
    async (campaign: Campaign, next: CampaignStatus): Promise<void> => {
      setApiError(null);
      setStatus(null);
      setStatusId(campaign.id);
      try {
        const result = await apiSetCampaignStatus({
          campaignId: campaign.id,
          status: next,
        });
        const e = asError(result);
        if (e !== null) {
          setApiError(toUserMessage(e));
          return;
        }
        await refresh();
      } catch {
        setApiError(networkError("update the campaign status"));
      } finally {
        setStatusId(null);
      }
    },
    [refresh],
  );

  const handleRecord = useCallback(
    async (
      campaign: Campaign,
      input: {
        impressions?: number;
        clicks?: number;
        conversions?: number;
        spendMinor?: string;
      },
    ): Promise<void> => {
      setApiError(null);
      setStatus(null);
      setStatsId(campaign.id);
      try {
        const result = await apiRecordCampaignStats({
          campaignId: campaign.id,
          ...input,
        });
        const e = asError(result);
        if (e !== null) {
          setApiError(toUserMessage(e));
          return;
        }
        setStatus({
          what: "Campaign stats recorded.",
          why: `"${campaign.name}" now reflects the added performance.`,
          next: "No action required.",
        });
        await refresh();
      } catch {
        setApiError(networkError("record the campaign stats"));
      } finally {
        setStatsId(null);
      }
    },
    [refresh],
  );

  const handleDeleteCampaign = useCallback(
    async (campaign: Campaign): Promise<void> => {
      setApiError(null);
      setStatus(null);
      setDeletingCampaignId(campaign.id);
      try {
        const result = await apiDeleteCampaign(campaign.id);
        const e = asError(result);
        if (e !== null) {
          setApiError(toUserMessage(e));
          return;
        }
        setStatus({
          what: "Campaign deleted.",
          why: `"${campaign.name}" has been removed.`,
          next: "No action required.",
        });
        await refresh();
      } catch {
        setApiError(networkError("delete the campaign"));
      } finally {
        setDeletingCampaignId(null);
      }
    },
    [refresh],
  );

  const handleCreatePage = useCallback(
    async (input: {
      title: string;
      slug?: string;
      metaDescription?: string;
      heading?: string;
      body?: string;
    }): Promise<void> => {
      setApiError(null);
      setStatus(null);
      setCreatePageBusy(true);
      try {
        const result = await apiCreateLandingPage(input);
        const e = asError(result);
        if (e !== null) {
          setApiError(toUserMessage(e));
          return;
        }
        setStatus({
          what: "Landing page created.",
          why: "The page has been added (unpublished).",
          next: "Audit its SEO, then publish it when ready.",
        });
        await refresh();
      } catch {
        setApiError(networkError("create the landing page"));
      } finally {
        setCreatePageBusy(false);
      }
    },
    [refresh],
  );

  const handlePublish = useCallback(
    async (page: LandingPage, published: boolean): Promise<void> => {
      setApiError(null);
      setStatus(null);
      setPublishId(page.id);
      try {
        const result = await apiSetPagePublished({
          pageId: page.id,
          published,
        });
        const e = asError(result);
        if (e !== null) {
          setApiError(toUserMessage(e));
          return;
        }
        await refresh();
      } catch {
        setApiError(networkError("update the page"));
      } finally {
        setPublishId(null);
      }
    },
    [refresh],
  );

  const handleAudit = useCallback(async (page: LandingPage): Promise<void> => {
    setApiError(null);
    setStatus(null);
    setAuditId(page.id);
    try {
      const result = await apiAuditPage(page.id);
      const e = asError(result);
      if (e !== null) {
        setApiError(toUserMessage(e));
        return;
      }
      setAudits((prev) => ({ ...prev, [page.id]: result as SeoAudit }));
    } catch {
      setApiError(networkError("audit the page"));
    } finally {
      setAuditId(null);
    }
  }, []);

  const handleDeletePage = useCallback(
    async (page: LandingPage): Promise<void> => {
      setApiError(null);
      setStatus(null);
      setDeletingPageId(page.id);
      try {
        const result = await apiDeleteLandingPage(page.id);
        const e = asError(result);
        if (e !== null) {
          setApiError(toUserMessage(e));
          return;
        }
        setStatus({
          what: "Landing page deleted.",
          why: `"${page.title}" has been removed.`,
          next: "No action required.",
        });
        await refresh();
      } catch {
        setApiError(networkError("delete the page"));
      } finally {
        setDeletingPageId(null);
      }
    },
    [refresh],
  );

  return (
    <section aria-labelledby="marketing-heading" data-console-screen="marketing">
      <p className="console-page-eyebrow">Demand · Marketing</p>
      <h1 id="marketing-heading">Marketing &amp; SEO</h1>
      <p className="console-lede">
        Campaigns, landing pages, and SEO tooling to grow direct demand for your
        properties. Track campaign performance and cost, and keep your landing
        pages optimized for search.
      </p>

      {state.kind === "loading" ? (
        <p className="console-loading" role="status" aria-live="polite">
          Loading marketing data…
        </p>
      ) : null}

      {state.kind === "error" ? (
        <p className="console-empty" role="status">
          Marketing data is unavailable right now. Try refreshing in a moment.
        </p>
      ) : null}

      {state.kind === "ready" ? (
        <>
          {apiError !== null ? (
            <UserMessage message={apiError} severity="error" />
          ) : null}
          {status !== null ? (
            <UserMessage message={status} severity="status" />
          ) : null}

          <CampaignsPanel
            campaigns={state.campaigns}
            filter={filter}
            onFilter={setFilter}
            onCreate={(input) => void handleCreateCampaign(input)}
            onStatus={(campaign, next) => void handleStatus(campaign, next)}
            onRecord={(campaign, input) => void handleRecord(campaign, input)}
            onDelete={(campaign) => void handleDeleteCampaign(campaign)}
            createBusy={createCampaignBusy}
            statusId={statusId}
            statsId={statsId}
            deletingId={deletingCampaignId}
          />

          <PagesPanel
            pages={state.pages}
            audits={audits}
            onCreate={(input) => void handleCreatePage(input)}
            onPublish={(page, published) => void handlePublish(page, published)}
            onAudit={(page) => void handleAudit(page)}
            onDelete={(page) => void handleDeletePage(page)}
            createBusy={createPageBusy}
            publishId={publishId}
            auditId={auditId}
            deletingId={deletingPageId}
          />
        </>
      ) : null}
    </section>
  );
}

/** A consistent network-failure UserMessage for a failed action. */
function networkError(action: string): UserMessageModel {
  return {
    what: `Could not ${action}.`,
    why: "The request to the marketing service failed.",
    next: "Check the API server is running and try again.",
  };
}
